import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import usePollingConnection, {
  PollingConnectionBaseState,
} from '#/utils/usePollingConnection';

import { Balance, BalancesResp, getBalances } from '#/api/balances';

import { useWalletView } from '#/features/wallet/wallet-context';

import { balanceView, BalanceView } from './balance-view';

const adapter = createEntityAdapter<Balance>({
  selectId: (balance) => balance.token,
  sortComparer: (a, b) => b.token.localeCompare(a.token),
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: BalancesStoreState = {
  status: 'connected',
  error: '',
  balances: adapter.getInitialState(),
};

interface BalancesStoreState extends PollingConnectionBaseState {
  readonly balances: EntityState<Balance>;
}

export interface BalancesStoreView extends PollingConnectionBaseState {
  readonly getBalance: (token: Balance['token']) => Balance | null;
  readonly getAllBalances: () => Balance[];
}

export interface BalancesView extends Omit<BalancesStoreState, 'balances'> {
  readonly balances: readonly BalanceView[];
}

export const [useBalancesView, BalancesViewProvider] =
  createSafeContext<BalancesView>('BalancesView');

export const [useBalancesStore, BalancesStoreViewProvider] =
  createSafeContext<BalancesStoreView>('BalancesStoreView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function BalancesStoreProvider(props: ProviderProps) {
  const [store, setStore] = useState(INITIAL_STATE);
  const storeView = useMemo(() => createStoreView(store), [store]);

  useBalanceFetch(setStore);

  return (
    <BalancesStoreViewProvider value={storeView}>
      {props.children}
    </BalancesStoreViewProvider>
  );
}

export function BalancesProvider(props: ProviderProps) {
  const storeView = useBalancesStore();
  const view = useMemo(() => createView(storeView), [storeView]);

  return (
    <BalancesViewProvider value={view}>{props.children}</BalancesViewProvider>
  );
}

export function createStoreView(store: BalancesStoreState): BalancesStoreView {
  return {
    status: store.status,
    error: store.error,
    getAllBalances: () => selectors.selectAll(store.balances),
    getBalance: (token) => selectors.selectById(store.balances, token) ?? null,
  };
}

export function createView(store: BalancesStoreView): BalancesView {
  const allBalances = store.getAllBalances();
  const balances = allBalances.map((balance) => balanceView(balance));

  return { status: store.status, error: store.error, balances };
}

const REFETCH_INTERVAL = ENVIRONMENT === 'test-runner' ? 50 : 5000;

function useBalanceFetch(
  setState: Dispatch<SetStateAction<BalancesStoreState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;
  const refetchInterval = REFETCH_INTERVAL;

  const fetchAll = useCallback(
    async (signal: AbortSignal) => getBalances({ signal }),
    [],
  );

  const setAll = useCallback(
    (data: BalancesResp) => {
      setState((state) => ({
        ...state,
        balances: adapter.setAll(state.balances, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      balances: adapter.removeAll(state.balances),
    }));
  }, [setState]);

  usePollingConnection<BalancesResp, BalancesStoreState>({
    setState,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    refetchInterval,
  });
}
